import api from '@/api';
import normalise from '@/app/util/normalise';

// Pagination metadata from response headers, with JSON:API meta + params fallbacks.
function paginationFromResponse(response, params = {}) {
  const headers = response.headers || {};
  const meta = response.data.meta || {};
  return {
    page: Number.parseInt(headers['current-page'], 10) || meta.page || params.page || 1,
    per_page: Number.parseInt(headers['per-page'], 10) || meta.per_page || params.per_page || 10,
    total_pages: Number.parseInt(headers['total-pages'], 10) || meta.total_pages || 0,
    total_count: Number.parseInt(headers['total-count'], 10) || meta.total_count || 0,
  };
}

const RECORD_ACTIONS = {
  NEW_RECORD(endpoint) {
    return async () => {
      const r = await api.get(`${endpoint}new`);
      return r.data.data;
    };
  },

  FETCH_RECORD(endpoint, schema) {
    // TEMPORARY WORKAROUND (EM-685): skip_404_handler lets callers swallow the global
    // 404 handler so a component can render a fallback for a deleted/discarded record
    // (e.g. a removed sales contact in SelectContact). Remove once EM-685 lands a durable
    // strategy for handling deleted users.
    return async ({ rootState, commit }, { id, skip_404_handler = false }) => {
      const { namespace } = rootState.shared.current;
      const r = await api.get(endpoint + id, { skip_404_handler });
      const json = normalise(r);
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) commit('MERGE_RECORDS', { records, module, attr, namespace });
      });
      const records = json[schema.record_type];
      const attr = schema.store_attr;
      const module = schema.store_module;
      if (records) commit('MERGE_RECORDS', { records, module, attr, namespace });
      return records ? records[id] : null;
    };
  },
  // merges included record(s) in schema (if present), afterwards syncs primary record(s)
  FETCH_RECORDS(endpoint, schema) {
    return async ({ rootState, commit }, { purge = false, ...params } = {}) => {
      let { namespace } = rootState.shared.current;
      const r = await api.get(endpoint, { params });
      const record_ids_new = _.map(r.data.data, 'id');
      const json = normalise(r);
      const included = {};
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) commit('MERGE_RECORDS', { records, module, attr, namespace });
        included[resource.record_type] = json[resource.record_type] || {};
      });
      const records = json[schema.record_type];
      const attr = schema.store_attr;
      const module = schema.store_module;
      if (schema.namespace) {
        // override namespace in case of shared modules (feature_toggles)
        ({ namespace } = schema);
      }
      if (purge) {
        commit('PURGE_RECORDS', {
          ids: _.map(rootState[namespace][module][attr], 'id'),
          module,
          attr,
          namespace,
        });
      }

      if (records) commit('SYNC_RECORDS', { records, module, attr, params, namespace });
      // Return both primary and included records
      return records ? Object.values(_.map(record_ids_new, (id) => records[id])) : [];
    };
  },

  FETCH_RECORDS_PAGE(endpoint, schema, options = {}) {
    const purge = _.get(options, 'purge', true);
    return async ({ rootState, commit }, params) => {
      const record_sub_type = _.get(params, 'record_sub_type');
      delete params.record_sub_type;
      const { namespace } = rootState.shared.current;
      const r = await api.get(endpoint, { params });
      // normalise will not preserve the order of records, save ids' order before normalization
      const record_ids_page_new = _.map(r.data.data, 'id');
      const json = normalise(r);
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) commit('MERGE_RECORDS', { records, module, attr, namespace }, { root: true });
      });
      const records = json[schema.record_type] || {};
      const records_page = _.compact(_.at(records, record_ids_page_new));
      const attr = schema.store_attr;
      const attr_typed = record_sub_type ? `${record_sub_type}_${attr}` : attr;
      const attr_page = `${attr_typed}_page`;
      const module = schema.store_module;
      const record_ids_page_old = _.map(rootState[namespace][module][attr_page], 'id');
      const record_ids_to_purge = _.difference(record_ids_page_old, record_ids_page_new);
      // set new page
      commit('SET_RECORDS_PAGE', { records_page, module, attr_page, params, namespace }, { root: true });
      // add records of new page to general collection
      commit('MERGE_RECORDS', { records, module, attr, params, namespace }, { root: true });
      // remove records of old page from general collection
      // ENG-3179: ideally we also want to purge associated records to minimize vuex caching overhead/memory consumption for long sessions
      //  if we would have actual relationship_ids according to JSON:API spec, we could rely on these to also purge associated reocrds
      //  however, caveat: there might be other still present records whom also have associated records who could rely on overlapping records
      //    TBD if this is actual likely use case or if we can assume there's no overlap and can safely use this approach
      //      is this hypothetically a possible use case within a single "page"? I can't come up with an example for now
      if (purge) {
        commit('PURGE_RECORDS', {
          ids: record_ids_to_purge,
          module,
          attr,
          namespace,
        });
      }

      // Keep pagination and meta on this exact array; map/filter/spread copies drop these non-index properties.
      records_page.pagination = paginationFromResponse(r, params);
      records_page.meta = r.data.meta;

      return records_page;
    };
  },

  // merges included record(s) in schema (if present), afterwards merges primary record
  CREATE_RECORD(endpoint, schema) {
    return async ({ rootState, commit }, { record, skip_global_error_handler = false }) => {
      const { namespace } = rootState.shared.current;
      const r = await api.post(endpoint, { data: record }, { skip_global_error_handler });
      const json = normalise(r);
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) {
          commit('MERGE_RECORDS', { records, module, attr, namespace });
        }
      });
      const records = json[schema.record_type];
      const attr = schema.store_attr;
      const module = schema.store_module;
      if (records) commit('MERGE_RECORDS', { records, module, attr, namespace });
      return records ? Object.values(records)[0] : null;
    };
  },

  // create(s) record(s) with same attributes, afterwards merges primary record(s)
  CREATE_RECORDS_ATTRIBUTES(endpoint, schema) {
    return async ({ rootState, commit }, { attributes, ids, extras, skip_global_error_handler = false }) => {
      const { namespace } = rootState.shared.current;
      const r = await api.post(
        `${endpoint}bulk_create_attributes`,
        {
          data: attributes,
          ids,
          extras,
        },
        { skip_global_error_handler },
      );
      const json = normalise(r);
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) {
          commit('MERGE_RECORDS', { records, module, attr, namespace });
        }
      });
      const records = json[schema.record_type];
      const attr = schema.store_attr;
      const module = schema.store_module;
      if (records) commit('MERGE_RECORDS', { records, module, attr, namespace });
      return records ? Object.values(records) : null;
    };
  },

  // create(s) record(s) with different attributes, afterwards merges primary record(s)
  CREATE_RECORDS(endpoint, schema) {
    return async ({ rootState, commit }, { objects }) => {
      const { namespace } = rootState.shared.current;
      const r = await api.post(`${endpoint}bulk_create`, { data: objects });
      const json = normalise(r);
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) {
          commit('MERGE_RECORDS', { records, module, attr, namespace });
        }
      });
      const records = json[schema.record_type];
      const attr = schema.store_attr;
      const module = schema.store_module;
      if (records) commit('MERGE_RECORDS', { records, module, attr, namespace });
      return records ? Object.values(records) : null;
    };
  },

  // merges included record(s) in schema (if present), afterwards merges primary record
  UPDATE_RECORD(endpoint, schema) {
    return async ({ rootState, commit }, { record, params, skip_global_error_handler = false }) => {
      const { namespace } = rootState.shared.current;
      delete record.decorated;
      const r = await api.patch(endpoint + record.id, { data: record }, { params, skip_global_error_handler });
      const json = normalise(r);
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) {
          commit('MERGE_RECORDS', { records, module, attr, namespace });
          const attr_page = `${attr}_page`;
          if (resource.is_paginated || !_.isEmpty(rootState[namespace][module][attr_page])) {
            const [includedRecord] = _.values(records);
            commit('MERGE_RECORD_PAGE', {
              record: includedRecord,
              module,
              attr_page,
              namespace,
            });
          }
        }
      });
      const records = json[schema.record_type];
      const [updatedRecord] = _.values(records);
      const attr = schema.store_attr;
      const attr_page = `${attr}_page`;
      const module = schema.store_module;
      if (records) {
        commit('MERGE_RECORDS', { records, module, attr, namespace });
        if (schema.is_paginated || !_.isEmpty(rootState[namespace][module][attr_page])) {
          commit('MERGE_RECORD_PAGE', {
            record: updatedRecord,
            module,
            attr_page,
            namespace,
          });
        }
      }
      return updatedRecord;
    };
  },

  UPDATE_RECORDS(endpoint) {
    return async (_, { objects }) => {
      if (objects.length === 0) return;

      await api.post(`${endpoint}bulk_update`, { data: objects });
    };
  },

  // updates all given record(s) with same set of attributes, afterwards syncs primary record(s)
  UPDATE_RECORDS_ATTRIBUTES(endpoint, schema) {
    return async ({ rootState, commit }, { attributes, ids, type }) => {
      const { namespace } = rootState.shared.current;
      const r = await api.post(`${endpoint}bulk_update_attributes`, {
        data: attributes,
        ids,
        type,
      });
      const json = normalise(r);
      schema.included.forEach((resource) => {
        const records = json[resource.record_type] || null;
        const attr = resource.store_attr;
        const module = resource.store_module;
        if (records) commit('MERGE_RECORDS', { records, module, attr, namespace });
      });
      const records = json[schema.record_type];
      const attr = schema.store_attr;
      const attr_page = `${attr}_page`;
      const module = schema.store_module;
      if (records) {
        commit('SYNC_RECORDS', {
          records,
          module,
          attr,
          params: ids,
          namespace,
        });
      }
      if (!_.isEmpty(rootState[namespace][module][attr_page])) {
        Object.values(records).forEach((record) =>
          commit('MERGE_RECORD_PAGE', { record, module, attr_page, namespace }),
        );
      }
      return records ? Object.values(records)[0] : null;
    };
  },

  // params are needed to identify the client on supplied side
  // when deleting as a partner, the client_id gets added as params
  DELETE_RECORD(endpoint, schema) {
    return async ({ rootState, commit }, { record, params, skip_global_error_handler = false }) => {
      let { namespace } = rootState.shared.current;
      await api.delete(endpoint + record.id, { params, skip_global_error_handler });
      const attr = schema.store_attr;
      const module = schema.store_module;
      if (schema.namespace) {
        // override namespace in case of shared modules (feature_toggles)
        ({ namespace } = schema);
      }
      commit('PURGE_RECORD', { record, module, attr, namespace });
      return record;
    };
  },

  DELETE_RECORDS(endpoint, schema) {
    return async ({ rootState, commit }, { ids }) => {
      const { namespace } = rootState.shared.current;
      await api.delete(`${endpoint}bulk_destroy`, { params: { ids } });
      const attr = schema.store_attr;
      const module = schema.store_module;
      commit('PURGE_RECORDS', { ids, module, attr, namespace });
      return null;
    };
  },

  PURGE_RECORDS(endpoint, schema) {
    return ({ rootState, commit }, { ids }) => {
      const { namespace } = rootState.shared.current;
      const attr = schema.store_attr;
      const module = schema.store_module;
      commit('PURGE_RECORDS', { ids, module, attr, namespace });
      return null;
    };
  },
};

export { paginationFromResponse };
export default RECORD_ACTIONS;
